import { useModeSelect } from "@/hooks/useModeSelect";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
} from "./ui/alert-dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { Button } from "./ui/button";
import { orderTypeOptions } from "@/lib/orderTypes";
import { locationRoute } from "@/App";
import { useLocationMenus } from "@/hooks/useLocationMenus";
import { useTables } from "@/hooks/useTables";

import { cn } from "@/lib/utils";
import { useMemo, useState } from "react";
import { useLocation } from "@/providers/LocationContextProvider";
import { MenuType } from "@/types/menu";

export function ModeSelector() {
  const { showSelector, setShowSelector } = useModeSelect();
  const { locationId } = locationRoute.useParams();
  const { orderType, tableId, setTableId, setOrderType } = useLocation();
  const [selectedTableId, setSelectedTableId] = useState<string | null>(
    tableId
  );
  const { hasTakeAwayMenu, hasDineInMenu } = useLocationMenus();
  const { data: tables } = useTables(locationId, true);
  const enabledOptions = orderTypeOptions.filter((option) => {
    if (option.value === "TAKE_AWAY") {
      return hasTakeAwayMenu;
    }

    if (option.value === "DINE_IN") {
      return hasDineInMenu;
    }

    return false;
  });

  const defaultOrderType =
    enabledOptions?.length === 1 ? enabledOptions?.[0]?.value : null;

  const [newOrderType, setNewOrderType] = useState<MenuType | null>(
    orderType || defaultOrderType
  );

  const handleCancelChange = () => {
    setShowSelector(false);
  };

  const handleConfirmChange = () => {
    setShowSelector(false);

    if (newOrderType !== orderType) {
      console.log("Changing order type from", orderType, "to", newOrderType);
      setOrderType(newOrderType); // Only set order type if it's different from the current one
    }

    if (newOrderType === "DINE_IN") {
      if (selectedTableId) {
        setTableId(selectedTableId);
      }
    }
  };

  const handleTableSelect = (value: string) => {
    setSelectedTableId(value);
  };

  const canConfirm = useMemo(() => {
    if (newOrderType === "DINE_IN") {
      return selectedTableId !== null && selectedTableId !== "";
    }
    return true; // For other order types, no table selection is needed
  }, [newOrderType, selectedTableId]);

  return (
    <AlertDialog open={showSelector} onOpenChange={setShowSelector}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <h2 className="text-lg font-medium tracking-tighter">
            How would you like to order?
          </h2>
        </AlertDialogHeader>

        <Tabs
          defaultValue={orderType ? orderType : undefined}
          value={newOrderType ? newOrderType : undefined}
          className="w-full"
          onValueChange={(value) => {
            setNewOrderType(value as MenuType);
          }}
        >
          <TabsList
            className={cn(
              "w-full grid h-16 gap-4 border rounded-md bg-background",
              enabledOptions.length > 2 ? "grid-cols-3" : "grid-cols-2",
              enabledOptions.length === 1 && "hidden"
            )}
          >
            {enabledOptions.map((option) => (
              <TabsTrigger
                key={option.value}
                value={option.value}
                className="text-left"
              >
                {option.icon}
                <span>{option.label}</span>
              </TabsTrigger>
            ))}
          </TabsList>
          {enabledOptions.map((option) => (
            <TabsContent key={option.value} value={option.value}>
              {option.value === "DINE_IN" && (
                <div className="flex flex-col gap-y-2 py-2">
                  <div className="text-sm text-muted-foreground mb-2 text-center">
                    Please select a table to continue.
                  </div>
                  <select
                    className="border-primary bg-background text-foreground border-2 rounded-md p-2 w-full text-center"
                    value={selectedTableId ? selectedTableId : ""}
                    onChange={(e) => handleTableSelect(e.target.value)}
                  >
                    <option disabled value="">
                      Please select a table
                    </option>
                    {tables?.map((table) => (
                      <option key={table.name} value={table.id}>
                        {table.tableIdentifier}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </TabsContent>
          ))}
        </Tabs>

        <AlertDialogFooter className="flex flex-col gap-y-2 mt-auto">
          <Button
            onClick={() => handleConfirmChange()}
            disabled={canConfirm === false}
          >
            Confirm change
          </Button>
          <Button onClick={() => handleCancelChange()} variant={"outline"}>
            Cancel
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
